import { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { useNotification } from "../context/NotificationContext";

import { useAuth } from "../services/AuthService";

export default function ResetPasswordPage() {
  const { showNotification } = useNotification();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { resetPassword, loading, clearError } = useAuth();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";

  useEffect(() => {
    window.scrollTo(0, 0);
    clearError();
  }, [clearError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      showNotification({
        type: "failed",
        title: "Mismatch",
        message: "Passwords do not match."
      });
      return;
    }

    if (password.length < 8) {
      showNotification({
        type: "failed",
        title: "Weak Password",
        message: "Password must be at least 8 characters."
      });
      return;
    }

    try {
      const result = await resetPassword(email, token, password);
      if (result.status === "success") {
        setIsSuccess(true);
        showNotification({
          type: "success",
          title: "Password Reset",
          message: "Your password has been reset successfully."
        });
      } else {
        showNotification({
          type: "failed",
          title: "Error",
          message: result.message || "Failed to reset password."
        });
      }
    } catch (err) {
      showNotification({
        type: "failed",
        title: "Error",
        message: err.response?.data?.message || "An unexpected error occurred."
      });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO title="Reset Password" url="/reset-password" />
      <JazzHeader />

      <main className="flex-1 flex items-center justify-center p-4 py-12 md:py-20 bg-[#fafafa] relative overflow-hidden">
        {/* Subtle background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-jazz-brown/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-jazz-brown/5 rounded-full blur-3xl"></div>
        </div>

        <div className="w-full max-w-md relative animate-fade-in">
          {/* Card Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-white border border-black/5 flex items-center justify-center shadow-sm mx-auto mb-6 rounded-2xl">
              <img src="/static/images/temb_music_logo.png" alt="Temidayo Balogun Logo" className="w-12 h-12 object-contain" />
            </div>
            <h1 className="text-4xl font-serif italic text-black mb-2">
              Reset Password
            </h1>
            {!isSuccess && (
              <p className="text-xs font-bold tracking-[1px] text-black/60 uppercase">
                Setting a new password for <span className="text-jazz-brown">{email}</span>
              </p>
            )}
          </div>

          {/* Card */}
          <div className="bg-white rounded-[2rem] shadow-xl border border-black/5 p-8 md:p-10">
            {isSuccess ? (
              <div className="text-center space-y-6 py-4 animate-fade-in">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-100">
                  <CheckCircle className="text-green-600" size={40} />
                </div>
                <div>
                  <h2 className="text-2xl font-serif italic text-black mb-3">Successfully Reset!</h2>
                  <p className="text-[10px] font-bold tracking-[1px] uppercase text-black/60 leading-relaxed">
                    Your password has been securely updated. You can now use your new password to sign in.
                  </p>
                </div>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 w-full py-5 px-6 bg-black text-white font-bold text-[10px] tracking-[3px] uppercase rounded-xl shadow-lg hover:bg-jazz-brown transform hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                  Go to Login <ArrowRight size={16} />
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">

                {/* New Password */}
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold tracking-[2px] uppercase text-black/60 ml-1">
                    New Password
                  </label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40 group-focus-within:text-jazz-brown transition-colors" size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 bg-[#fafafa] border border-black/5 rounded-xl text-sm font-semibold text-black placeholder-black/40 focus:outline-none focus:border-jazz-brown transition-all"
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-black/40 hover:text-jazz-brown transition-colors p-1"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold tracking-[2px] uppercase text-black/60 ml-1">
                    Confirm New Password
                  </label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40 group-focus-within:text-jazz-brown transition-colors" size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 bg-[#fafafa] border border-black/5 rounded-xl text-sm font-semibold text-black placeholder-black/40 focus:outline-none focus:border-jazz-brown transition-all"
                      placeholder="Repeat your password"
                      required
                    />
                  </div>
                </div>

                <div className="px-1 border-t border-black/5 pt-4">
                  <p className="text-[9px] font-bold uppercase tracking-[1px] text-black/60 leading-relaxed text-center">
                    Make sure your new password is secure and not used elsewhere.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading || password !== confirmPassword || password.length < 8}
                  className="w-full py-5 px-6 bg-black text-white font-bold text-[10px] tracking-[3px] uppercase rounded-xl shadow-lg hover:bg-jazz-brown transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Updating Password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>

                <Link
                  to="/login"
                  className="block text-center text-black/60 text-[10px] font-bold tracking-[1px] uppercase hover:text-jazz-brown transition-colors"
                >
                  Cancel and return to login
                </Link>
              </form>
            )}
          </div>

          <p className="text-center text-[9px] font-bold tracking-[2px] uppercase text-black/60 mt-12 leading-relaxed">
            &copy; {new Date().getFullYear()} TEMB Music. All rights reserved.
          </p>
        </div>
      </main>

      <JazzFooter />

      <style jsx="true" global="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out both;
        }
      `}</style>
    </div>
  );
}
